import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/lib/auth-context";
import type { MdfRequestComment } from "./types";

// POST /api/portal/mdf/requests/:id/comments — partner posts a comment on
// their own submitted request (pending/approved/declined). Optimistic
// append (mirror bucket 3 deal-comment pattern).

type Vars = { requestId: string; body: string };
type Context = {
  previous: MdfRequestComment[] | undefined;
  key: ReturnType<typeof queryKeys.mdf.requestComments>;
};

export function useAddMdfComment() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation<{ id: string }, ApiClientError, Vars, Context>({
    mutationFn: ({ requestId, body }) =>
      apiFetch<{ id: string }>(
        `/portal/mdf/requests/${requestId}/comments`,
        {
          method: "POST",
          body: JSON.stringify({ body }),
        },
      ),
    onMutate: async ({ requestId, body }) => {
      const key = queryKeys.mdf.requestComments(requestId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<MdfRequestComment[]>(key);
      const optimistic: MdfRequestComment = {
        id: `temp-${crypto.randomUUID()}`,
        request_id: requestId,
        author_user_id: session?.userId ?? null,
        author_role: "partner",
        body,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<MdfRequestComment[]>(key, (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to post comment");
    },
    onSettled: (_data, _err, { requestId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.mdf.requestComments(requestId),
      });
    },
  });
}
